/*
 * W-d4a5a81b: for ~90 s on 2026-09-30 every World read the Desk made waited its
 * 10 s limit and was answered 502 `transport`. Doors swallowed the failure and
 * looked loaded but empty, so the person was never told. Every World read goes
 * through fetchPrimary, which reports each /api/neucleos response here; while a
 * route's last answer was the Desk's `transport` refusal (the World did not
 * answer in time, or the connection to it failed), the Desk says so once, in
 * plain words (WorldNotAnsweringNotice). A typed refusal is the World's answer
 * and never counts; nor does a `transport` refusal the Desk wrote for its own
 * validation ("returned a malformed receipt"): only the transport's
 * not-answering sentences (@cleo/contracts worldNotAnswering) do. Answers are
 * applied per route in the order they arrived, so a slow-to-parse 502 cannot
 * overwrite a later success. A later success on the same route clears it; with no new
 * failure for QUIET_MS it fades, so a door that never re-reads cannot pin it.
 */

import { isWorldNotAnsweringReason } from "@cleo/contracts";

export const WORLD_NOT_ANSWERING_MESSAGE =
  "The World is not answering right now, so some parts of the Desk may be empty or out of date. Your work on this Mac is safe. This note goes away when the World answers again.";

const QUIET_MS = 90_000;
const WORLD_ROUTE = /^\/api\/neucleos\//;

export interface WorldReachability {
  /** True while a World read's last answer was the World not answering. */
  readonly notAnswering: boolean;
  /** When the most recent such answer arrived, or null. */
  readonly lastFailureAt: number | null;
}

const ANSWERING: WorldReachability = { notAnswering: false, lastFailureAt: null };
const failing = new Map<string, number>();
/** Per route, the arrival order of the newest answer applied. */
const applied = new Map<string, number>();
let arrivals = 0;
const listeners = new Set<() => void>();
let snapshot: WorldReachability = ANSWERING;
let quietTimer: ReturnType<typeof setTimeout> | null = null;

function publish(now: number): void {
  for (const [route, at] of failing) if (now - at >= QUIET_MS) failing.delete(route);
  const last = failing.size === 0 ? null : Math.max(...failing.values());
  const next: WorldReachability =
    last === null ? ANSWERING : { notAnswering: true, lastFailureAt: last };
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer =
    last === null
      ? null
      : setTimeout(() => publish(Date.now()), Math.max(0, last + QUIET_MS - now));
  if (next.notAnswering === snapshot.notAnswering && next.lastFailureAt === snapshot.lastFailureAt)
    return;
  snapshot = next;
  for (const listener of listeners) listener();
}

function routeOf(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

/** Whether a Desk response body is its `transport` refusal: the World did not answer. */
export function isWorldNotAnsweringBody(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { ok?: unknown }).ok === false &&
    (body as { kind?: unknown }).kind === "transport" &&
    isWorldNotAnsweringReason((body as { reason?: unknown }).reason)
  );
}

/**
 * Record one World read's answer. Never alters or consumes the response the
 * caller reads: a 502 is read from a clone.
 */
export async function noteWorldResponse(
  path: string,
  response: Pick<Response, "ok" | "status" | "clone">,
  now: () => number = Date.now,
): Promise<void> {
  const route = routeOf(path);
  if (!WORLD_ROUTE.test(route)) return;
  const arrival = ++arrivals;
  if (response.ok) {
    applied.set(route, arrival);
    if (failing.delete(route)) publish(now());
    return;
  }
  // Any status: some routes answer a World timeout with their own status
  // (context selection), so the body's kind and reason decide.
  let body: unknown = null;
  try {
    body = await response.clone().json();
  } catch {
    return;
  }
  // A newer answer on this route already arrived while this body was read.
  if ((applied.get(route) ?? 0) > arrival) return;
  applied.set(route, arrival);
  if (!isWorldNotAnsweringBody(body)) {
    // A typed refusal is the World answering.
    if (failing.delete(route)) publish(now());
    return;
  }
  failing.set(route, now());
  publish(now());
}

export function subscribeWorldReachability(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getWorldReachability(): WorldReachability {
  return snapshot;
}

export function __resetWorldReachabilityForTests(): void {
  failing.clear();
  applied.clear();
  arrivals = 0;
  if (quietTimer !== null) clearTimeout(quietTimer);
  quietTimer = null;
  snapshot = ANSWERING;
  listeners.clear();
}
